import React from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  DiaryFilledIcon,
  DiaryIcon,
  HomeFilledIcon,
  HomeIcon,
  MailFilledIcon,
  MailIcon,
  StatsFilledIcon,
  StatsIcon,
  ThreeDotsIcon,
} from '@/components/Icons/SidebarIcons';
import { useUnseenEvents } from '@/hooks/useUnseenEvents';
import type { NavItem } from '@/types/components';
import {
  ROUTE_DIARY,
  ROUTE_HOME,
  ROUTE_MAIL,
  ROUTE_OTHER,
  ROUTE_OVERVIEW,
} from '@/router/routes';
import { getMailTabCount } from '@/utils/mail';

export const MobileNavbar: React.FC = () => {
  const { t } = useTranslation();
  const { data: unseenEvents } = useUnseenEvents({
    shouldRefetchOnMount: false,
  });

  const hasUnseenMail = getMailTabCount(unseenEvents) > 0;

  const tabs: NavItem[] = [
    {
      to: ROUTE_HOME,
      label: t('sidebar.tabs.home'),
      icon: <HomeIcon className="size-6" />,
      iconActive: <HomeFilledIcon className="size-6" />,
      end: true,
    },
    {
      to: ROUTE_DIARY,
      label: t('sidebar.tabs.diary'),
      icon: <DiaryIcon className="size-6" />,
      iconActive: <DiaryFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_OVERVIEW,
      label: t('sidebar.tabs.overview'),
      icon: <StatsIcon className="size-6" />,
      iconActive: <StatsFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_MAIL,
      label: t('sidebar.tabs.mail'),
      icon: <MailIcon className="size-6" />,
      iconActive: <MailFilledIcon className="size-6" />,
    },
    {
      to: ROUTE_OTHER,
      label: t('sidebar.tabs.other'),
      icon: <ThreeDotsIcon className="size-6" />,
    },
  ];

  return (
    <nav
      aria-label={t('sidebar.aria.nav')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-divider bg-card pb-[calc(8px+env(safe-area-inset-bottom))] md:hidden"
    >
      <ul className="flex">
        {tabs.map((tab) => (
          <li key={tab.to} className="flex-1">
            <NavLink
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                `flex h-14 cursor-pointer flex-col items-center justify-center gap-0.5 px-1 pt-1 ${
                  isActive ? 'text-primary' : 'text-subtle-foreground'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex items-center justify-center">
                    {isActive && tab.iconActive ? tab.iconActive : tab.icon}
                    {tab.to === ROUTE_MAIL && hasUnseenMail && (
                      <span className="absolute top-0.5 -right-[1.5px] size-2 rounded-full border-[1px] border-card bg-accent" />
                    )}
                  </span>
                  <span className="text-caption-10">{tab.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};
